import React, { type FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { QuizsetFilterCriteriaSearchType } from '@/api';
import { useEffectOnce } from '@/hooks';
import { ArrowLeftIcon, MagnifyingGlassIcon, XSmallCircleColorIcon } from '@/icons';
import { usePublicSearchFilterQuery } from '@/module/public/hooks/usePublicSearchFilterQuery';
import { useSearchQuizQuery } from '@/module/public/hooks/useSearchQuizQuery';
import { type QuizListSearchProps } from '@/shared/quizList/SearchField/SearchMetaBar';
import { Input } from '@/ui';
import { alertDialog, IconButton } from '@/ui_v3';
import { cn } from '@/utils';
import { QUIZ_SET_TITLE_MAX_LENGTH } from '@/views/build/form/schema';
import { SearchSuggestionPanel } from './SearchSuggestionPanel';
export const SearchInputQuizForm = ({
  placeholder,
  autoFocus = false,
  eventType,
  className,
  onBackClick,
  showSearchButton = true
}: Pick<QuizListSearchProps, 'eventType'> & {
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  onBackClick?: () => void;
  /**
   * 확정된 검색 상태(좌측 뒤로가기 화살표가 있는 디자인)에서는 우측 돋보기 제출 버튼을
   * 숨겨 Figma 디자인과 맞춘다. Enter 또는 추천 패널 선택으로 재검색한다.
   */
  showSearchButton?: boolean;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const {
    handleSearchText,
    searchText,
    handleSearchType
  } = useSearchQuizQuery();
  const {
    clearSearchFilters
  } = usePublicSearchFilterQuery();
  const [inputValue, setInputValue] = useState(searchText ?? '');
  const [isSuggestionPanelDismissed, setIsSuggestionPanelDismissed] = useState(false);
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const [isSuggestionPanelVisible, setIsSuggestionPanelVisible] = useState(false);
  const {
    t
  } = useTranslation(['common']);
  const shouldShowSuggestionPanel = eventType === 'public' && isSearchInputFocused && !isSuggestionPanelDismissed;
  useEffect(() => {
    if (!shouldShowSuggestionPanel) {
      setIsSuggestionPanelVisible(false);
    }
  }, [shouldShowSuggestionPanel]);
  const handleSubmitByText = (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!ref.current) {
      return;
    }
    if (ref.current.value?.trim().length === 1) {
      alertDialog.open({
        header: {
          title: t('public.error.searchLength')
        }
      });
      return;
    }
    if (!ref.current.value?.trim()) {
      clearSearchFilters();
      handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
    }
    setIsSuggestionPanelDismissed(true);
    setIsSearchInputFocused(false);
    ref.current.blur();
    logger.track('search_quiz', {
      searchText: ref.current.value,
      interactionType: e.type === 'click' ? 'click' : 'enter',
      eventType
    });
    handleSearchText(ref.current?.value);
  };
  const resetSearchText = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setInputValue('');
    setIsSuggestionPanelDismissed(false);
    setIsSearchInputFocused(true);
    handleSearchText('');
    clearSearchFilters();
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
    if (ref.current) {
      ref.current.value = '';
    }
  };
  const handleBackClick = () => {
    setInputValue('');
    setIsSuggestionPanelDismissed(true);
    setIsSearchInputFocused(false);
    if (ref.current) {
      ref.current.value = '';
      ref.current.blur();
    }
    onBackClick?.();
  };
  const selectTitleSuggestion = (title: string) => {
    setInputValue(title);
    setIsSuggestionPanelDismissed(true);
    setIsSearchInputFocused(false);
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
    handleSearchText(title);
    if (ref.current) {
      ref.current.value = title;
    }
  };
  const closeSearchInput = () => {
    setIsSuggestionPanelDismissed(true);
    setIsSearchInputFocused(false);
    ref.current?.blur();
  };
  const selectCategorySuggestion = () => {
    setInputValue('');
    setIsSuggestionPanelDismissed(true);
    setIsSearchInputFocused(false);
    handleSearchText('');
    handleSearchType(QuizsetFilterCriteriaSearchType.ALL);
    if (ref.current) {
      ref.current.value = '';
      ref.current.blur();
    }
  };
  useEffectOnce(() => {
    if (ref.current && searchText) {
      ref.current.value = searchText;
      setInputValue(searchText);
    }
    if (autoFocus) {
      // 모바일 경우
      setTimeout(() => {
        ref.current?.focus();
      }, 1000);
    }
  });
  useEffect(() => {
    if (ref.current) {
      ref.current.value = searchText ?? '';
    }
    setInputValue(searchText ?? '');
  }, [searchText]);
  return <form className={cn('relative size-full max-mobile-lg:w-full tablet-sm:h-[56px] tablet-sm:max-w-[606px]', className)} onSubmit={e => handleSubmitByText(e)} data-sentry-component="SearchInputQuizForm" data-sentry-source-file="SearchInputQuizForm.tsx">
      <Input name={'searchText'} size={'md'} placeholder={placeholder ?? t('common:public.search.placeholder3')} className={cn('size-full text-heading-sm-bold', eventType === 'public' && 'focus-within:!border-strong focus-within:!shadow-none focus-within:ring-1 focus-within:ring-inset focus-within:ring-strong', isSuggestionPanelVisible && '!rounded-b-none')} ref={ref} maxLength={QUIZ_SET_TITLE_MAX_LENGTH} onFocus={() => {
      setIsSearchInputFocused(true);
    }} onBlur={() => {
      window.setTimeout(() => setIsSearchInputFocused(false), 100);
    }} onChange={e => {
      setInputValue(e.target.value);
      setIsSuggestionPanelDismissed(false);
      setIsSearchInputFocused(true);
    }} leftSlot={onBackClick ? <IconButton type={'button'} variant="transparent" onClick={handleBackClick} size={'md'} intent={'neutral'} aria-label={'검색 닫기'}>
              <ArrowLeftIcon size={'md'} />
            </IconButton> : null} rightSlot={<div className={'flex gap-xs laptop-sm:gap-3xs'}>
            {inputValue && <IconButton type={'button'} variant="transparent" onClick={resetSearchText} size={'md'} intent={'neutral'}>
                <XSmallCircleColorIcon size={'md'} />
              </IconButton>}
            {showSearchButton && <IconButton onClick={e => {
        e.preventDefault();
        handleSubmitByText(e);
      }} type={'button'} variant="transparent" size={'md'} intent={'neutral'}>
                <MagnifyingGlassIcon size={'md'} />
              </IconButton>}
          </div>} data-sentry-element="Input" data-sentry-source-file="SearchInputQuizForm.tsx" />
      {shouldShowSuggestionPanel && <SearchSuggestionPanel query={inputValue} eventType={eventType} onAfterSelect={selectCategorySuggestion} onAfterTitleSelect={closeSearchInput} onSelectTitle={selectTitleSuggestion} onVisibilityChange={setIsSuggestionPanelVisible} />}
    </form>;
};